<template>
  <v-dialog v-model="internalDialog" max-width="1350" width="95%" scrollable persistent>
    <v-card class="rounded-xl overflow-hidden trip-report-card">
      <!-- Modal Header -->
      <v-card-title class="pa-4 purple darken-3 white--text d-flex justify-space-between align-center">
        <div class="d-flex align-center flex-wrap">
          <div class="d-flex align-center me-4">
            <v-icon color="white" large class="me-2">mdi-clipboard-text-search-outline</v-icon>
            <div>
              <div class="text-h6 font-weight-bold text-white">
                تقارير الرحلة — {{ reportTripTitle }}
              </div>
              <div class="caption font-weight-medium text-purple-lighten-4" v-if="trip || tripInfo">
                رقم الرحلة: <span class="font-weight-bold me-3">#{{ (tripInfo && tripInfo.id) ? tripInfo.id : (trip ? trip.id : '') }}</span>
                المسار: <span class="font-weight-bold me-3">{{ currentRouteName }}</span>
                وقت التشغيل: <span class="font-weight-bold me-3">{{ tConvert(currentFirstStopTime) }}</span>
                التكرار: <span class="font-weight-bold">{{ currentRepetitionLabel }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex align-center">
          <v-btn
            color="success darken-1"
            dark
            class="font-weight-bold elevation-2 me-3 text-none"
            :loading="loadingCsv"
            @click="exportCsv"
          >
            <v-icon left>mdi-file-delimited</v-icon>
            تصدير CSV
          </v-btn>

          <v-btn icon dark @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Main Scrollable Body -->
      <v-card-text class="pa-4 bg-grey-lighten-5">
        <!-- Error Alert Banner -->
        <v-alert v-if="errorMessage" type="error" dense class="mb-4 font-weight-bold d-flex align-center justify-space-between" style="opacity: 1 !important;">
          <div>{{ errorMessage }}</div>
          <v-btn x-small color="white" text class="font-weight-bold ms-3" @click="fetchReport">إعادة المحاولة</v-btn>
        </v-alert>

        <!-- Quick Period & Custom Date Filters -->
        <v-card outlined class="pa-3 mb-4 rounded-lg bg-white elevation-1">
          <div class="d-flex align-center justify-space-between flex-wrap gap-2 mb-3">
            <!-- Period Buttons -->
            <div class="d-flex align-center flex-wrap gap-1">
              <v-btn
                v-for="p in periodOptions"
                :key="p.value"
                small
                :color="selectedPeriod === p.value ? 'purple darken-2' : 'grey lighten-3'"
                :dark="selectedPeriod === p.value"
                class="font-weight-bold me-1 mb-1 btn-period"
                style="opacity: 1 !important; color: #111827;"
                :style="selectedPeriod === p.value ? 'color: #ffffff !important;' : ''"
                @click="selectPeriod(p.value)"
              >
                {{ p.label }}
              </v-btn>
            </div>

            <!-- Custom Date Inputs -->
            <div class="d-flex align-center flex-wrap gap-2" v-if="selectedPeriod === 'custom'">
              <v-text-field
                v-model="dateFrom"
                type="date"
                label="من تاريخ"
                dense
                outlined
                hide-details
                class="me-2 custom-date-input"
                style="max-width: 170px;"
                @change="fetchReport"
              ></v-text-field>

              <v-text-field
                v-model="dateTo"
                type="date"
                label="إلى تاريخ"
                dense
                outlined
                hide-details
                class="custom-date-input"
                style="max-width: 170px;"
                @change="fetchReport"
              ></v-text-field>
            </div>
          </div>

          <!-- Search & Additional Dropdown Filters -->
          <v-row dense class="align-center">
            <v-col cols="12" md="5">
              <v-text-field
                v-model="searchQuery"
                label="ابحث باسم أو رقم السائق أو العميل..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                hide-details
                clearable
                class="bg-white search-input-field"
                @input="debouncedFetch"
                @click:clear="debouncedFetch"
              ></v-text-field>
            </v-col>

            <v-col cols="12" sm="4" md="2">
              <v-select
                v-model="selectedDriver"
                :items="driverSelectItems"
                item-text="label"
                item-value="value"
                label="السائق"
                outlined
                dense
                hide-details
                @change="fetchReport"
              ></v-select>
            </v-col>

            <v-col cols="12" sm="4" md="2">
              <v-select
                v-model="selectedStatus"
                :items="statusOptions"
                item-text="label"
                item-value="value"
                label="حالة الرحلة"
                outlined
                dense
                hide-details
                @change="fetchReport"
              ></v-select>
            </v-col>

            <v-col cols="12" sm="4" md="3">
              <v-select
                v-model="selectedPaymentMethod"
                :items="paymentMethodOptions"
                item-text="label"
                item-value="value"
                label="وسيلة الدفع"
                outlined
                dense
                hide-details
                @change="fetchReport"
              ></v-select>
            </v-col>
          </v-row>
        </v-card>

        <!-- Loading Progress -->
        <v-progress-linear v-if="loading" indeterminate color="purple darken-2" class="mb-4 rounded"></v-progress-linear>

        <!-- Summary KPIs (8 Cards) -->
        <v-row dense class="mb-4" v-if="kpis">
          <v-col cols="6" sm="3" md="3" lg="1-5">
            <v-card outlined class="pa-3 rounded-lg text-center bg-white kpi-card border-purple">
              <div class="caption font-weight-bold text-grey-darken-2 mb-1">إجمالي مرات التشغيل</div>
              <div class="d-flex align-center justify-center">
                <v-icon color="purple darken-2" class="me-1">mdi-play-circle-outline</v-icon>
                <span class="text-h6 font-weight-bold text-dark">{{ kpis.total_executions }}</span>
              </div>
            </v-card>
          </v-col>

          <v-col cols="6" sm="3" md="3" lg="1-5">
            <v-card outlined class="pa-3 rounded-lg text-center bg-white kpi-card border-success">
              <div class="caption font-weight-bold text-grey-darken-2 mb-1">الرحلات المنفذة</div>
              <div class="d-flex align-center justify-center">
                <v-icon color="green darken-2" class="me-1">mdi-check-circle-outline</v-icon>
                <span class="text-h6 font-weight-bold text-success-dark">{{ kpis.executed_trips }}</span>
              </div>
            </v-card>
          </v-col>

          <v-col cols="6" sm="3" md="3" lg="1-5">
            <v-card outlined class="pa-3 rounded-lg text-center bg-white kpi-card border-error">
              <div class="caption font-weight-bold text-grey-darken-2 mb-1">غير المنفذة / الملغاة</div>
              <div class="d-flex align-center justify-center">
                <v-icon color="red darken-2" class="me-1">mdi-close-circle-outline</v-icon>
                <span class="text-h6 font-weight-bold text-error-dark">{{ kpis.not_executed_trips }}</span>
              </div>
            </v-card>
          </v-col>

          <v-col cols="6" sm="3" md="3" lg="1-5">
            <v-card outlined class="pa-3 rounded-lg text-center bg-white kpi-card border-info">
              <div class="caption font-weight-bold text-grey-darken-2 mb-1">إجمالي الحجوزات</div>
              <div class="d-flex align-center justify-center">
                <v-icon color="blue darken-2" class="me-1">mdi-account-group-outline</v-icon>
                <span class="text-h6 font-weight-bold text-dark">{{ kpis.total_bookings }}</span>
              </div>
            </v-card>
          </v-col>

          <v-col cols="6" sm="3" md="3" lg="1-5">
            <v-card outlined class="pa-3 rounded-lg text-center bg-white kpi-card border-cyan">
              <div class="caption font-weight-bold text-grey-darken-2 mb-1">إجمالي الركاب</div>
              <div class="d-flex align-center justify-center">
                <v-icon color="cyan darken-3" class="me-1">mdi-seat-passenger</v-icon>
                <span class="text-h6 font-weight-bold text-dark">{{ kpis.total_passengers }}</span>
              </div>
            </v-card>
          </v-col>

          <v-col cols="6" sm="3" md="3" lg="1-5">
            <v-card outlined class="pa-3 rounded-lg text-center bg-white kpi-card border-cash">
              <div class="caption font-weight-bold text-grey-darken-2 mb-1">تحصيل كاش</div>
              <div class="d-flex align-center justify-center">
                <v-icon color="teal darken-2" class="me-1">mdi-cash-multiple</v-icon>
                <span class="text-subtitle-1 font-weight-bold text-dark">{{ formatCurrency(kpis.total_cash) }}</span>
              </div>
            </v-card>
          </v-col>

          <v-col cols="6" sm="3" md="3" lg="1-5">
            <v-card outlined class="pa-3 rounded-lg text-center bg-white kpi-card border-online">
              <div class="caption font-weight-bold text-grey-darken-2 mb-1">تحصيل إلكتروني</div>
              <div class="d-flex align-center justify-center">
                <v-icon color="purple darken-1" class="me-1">mdi-credit-card-outline</v-icon>
                <span class="text-subtitle-1 font-weight-bold text-dark">{{ formatCurrency(kpis.total_other) }}</span>
              </div>
            </v-card>
          </v-col>

          <v-col cols="6" sm="3" md="3" lg="1-5">
            <v-card outlined class="pa-3 rounded-lg text-center bg-white kpi-card border-total">
              <div class="caption font-weight-bold text-grey-darken-2 mb-1">إجمالي التحصيلات</div>
              <div class="d-flex align-center justify-center">
                <v-icon color="indigo darken-3" class="me-1">mdi-wallet-outline</v-icon>
                <span class="text-subtitle-1 font-weight-bold text-purple-darken-4">{{ formatCurrency(kpis.grand_total) }}</span>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Navigation Tabs -->
        <v-card outlined class="rounded-lg bg-white elevation-1">
          <v-tabs v-model="activeTab" color="purple darken-3" grow class="border-bottom">
            <v-tab class="font-weight-bold">
              <v-icon left>mdi-file-table-outline</v-icon>
              تقرير الرحلات ({{ tripsPagination.total || 0 }})
            </v-tab>

            <v-tab class="font-weight-bold">
              <v-icon left>mdi-account-multiple-check-outline</v-icon>
              الحجوزات والركاب ({{ bookingsPagination.total || 0 }})
            </v-tab>

            <v-tab class="font-weight-bold">
              <v-icon left>mdi-account-tie-hat-outline</v-icon>
              تقرير السائقين ({{ driversSummaryList.length }})
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab">
            <!-- TAB 1: PlannedTrips Execution Table -->
            <v-tab-item class="pa-3">
              <v-simple-table class="report-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-center font-weight-bold"># / PlannedTrip ID</th>
                      <th class="text-center font-weight-bold">تاريخ الرحلة</th>
                      <th class="text-center font-weight-bold">وقت الرحلة</th>
                      <th class="text-center font-weight-bold">اسم السائق</th>
                      <th class="text-center font-weight-bold">الهاتف</th>
                      <th class="text-center font-weight-bold">الحالة</th>
                      <th class="text-center font-weight-bold">سبب عدم التنفيذ</th>
                      <th class="text-center font-weight-bold">عدد الحجوزات</th>
                      <th class="text-center font-weight-bold">عدد الركاب</th>
                      <th class="text-center font-weight-bold">الكاش</th>
                      <th class="text-center font-weight-bold">إلكتروني</th>
                      <th class="text-center font-weight-bold">الإجمالي</th>
                      <th class="text-center font-weight-bold">تفاصيل</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="pt in tripsTableRows">
                      <tr :key="pt.planned_trip_id" class="trip-main-row">
                        <td class="text-center font-weight-bold">#{{ pt.planned_trip_id }}</td>
                        <td class="text-center font-weight-bold">{{ pt.planned_date }}</td>
                        <td class="text-center font-weight-bold">{{ tConvert(pt.trip_time) }}</td>
                        <td class="text-center font-weight-bold primary--text">{{ pt.driver_name }}</td>
                        <td class="text-center font-weight-bold">{{ pt.driver_phone }}</td>
                        <td class="text-center">
                          <v-chip small class="font-weight-bold" :color="getStatusChipColor(pt.status)" dark style="opacity: 1 !important;">
                            {{ pt.status_label }}
                          </v-chip>
                        </td>
                        <td class="text-center caption font-weight-bold grey--text text--darken-3">{{ pt.cancellation_reason }}</td>
                        <td class="text-center font-weight-bold">{{ pt.bookings_count }}</td>
                        <td class="text-center font-weight-bold">{{ pt.passengers_count }}</td>
                        <td class="text-center font-weight-bold text-success-dark">{{ formatCurrency(pt.cash_total) }}</td>
                        <td class="text-center font-weight-bold text-purple-darken-3">{{ formatCurrency(pt.other_total) }}</td>
                        <td class="text-center font-weight-bold purple--text text--darken-4">{{ formatCurrency(pt.grand_total) }}</td>
                        <td class="text-center">
                          <v-btn
                            x-small
                            outlined
                            color="purple darken-2"
                            class="font-weight-bold"
                            @click="toggleExpandRow(pt)"
                          >
                            {{ expandedRowId === pt.planned_trip_id ? 'إخفاء التفاصيل' : 'عرض التفاصيل' }}
                            <v-icon x-small right>{{ expandedRowId === pt.planned_trip_id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                          </v-btn>
                        </td>
                      </tr>

                      <!-- Expanded In-Line Details Panel for Reservations -->
                      <tr v-if="expandedRowId === pt.planned_trip_id" :key="'expanded-' + pt.planned_trip_id" class="expanded-detail-row">
                        <td colspan="13" class="pa-3 bg-purple-lighten-5">
                          <v-card outlined class="pa-3 rounded-lg bg-white border-purple-light">
                            <div class="d-flex align-center justify-space-between mb-3">
                              <div class="subtitle-2 font-weight-bold text-purple-darken-4">
                                <v-icon small color="purple darken-3" class="me-1">mdi-account-group</v-icon>
                                تفاصيل حجوزات رحلة {{ pt.planned_date }} — {{ tConvert(pt.trip_time) }} (السائق: {{ pt.driver_name }})
                              </div>

                              <v-progress-circular v-if="loadingDetails" indeterminate size="20" width="2" color="purple darken-2"></v-progress-circular>
                            </div>

                            <v-simple-table dense class="inner-bookings-table" v-if="expandedDetails">
                              <thead>
                                <tr>
                                  <th class="text-center font-weight-bold"># / Reservation ID</th>
                                  <th class="text-center font-weight-bold">اسم العميل</th>
                                  <th class="text-center font-weight-bold">الهاتف</th>
                                  <th class="text-center font-weight-bold">عدد الركاب</th>
                                  <th class="text-center font-weight-bold">مقعد</th>
                                  <th class="text-center font-weight-bold">محطة الركوب</th>
                                  <th class="text-center font-weight-bold">محطة النزول</th>
                                  <th class="text-center font-weight-bold">وسيلة الدفع</th>
                                  <th class="text-center font-weight-bold">المبلغ</th>
                                  <th class="text-center font-weight-bold">حالة الدفع</th>
                                  <th class="text-center font-weight-bold">حالة الركوب</th>
                                  <th class="text-center font-weight-bold">وقت الحجز</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="res in expandedDetails.reservations" :key="res.reservation_id">
                                  <td class="text-center font-weight-bold">#{{ res.reservation_id }}</td>
                                  <td class="text-center font-weight-bold text-dark">{{ res.customer_name }}</td>
                                  <td class="text-center font-weight-bold text-dark">{{ res.customer_phone }}</td>
                                  <td class="text-center font-weight-bold">1</td>
                                  <td class="text-center caption font-weight-bold">{{ res.seat_number }}</td>
                                  <td class="text-center caption font-weight-bold primary--text">{{ res.start_stop }}</td>
                                  <td class="text-center caption font-weight-bold grey--text text--darken-3">{{ res.end_stop }}</td>
                                  <td class="text-center font-weight-bold">
                                    <v-chip x-small :color="res.payment_method === 'كاش' ? 'teal lighten-5 teal--text text--darken-4' : 'purple lighten-5 purple--text text--darken-4'" class="font-weight-bold" style="opacity: 1 !important;">
                                      {{ res.payment_method }}
                                    </v-chip>
                                  </td>
                                  <td class="text-center font-weight-bold">{{ formatCurrency(res.collected_amount || res.price) }}</td>
                                  <td class="text-center">
                                    <v-chip x-small class="font-weight-bold" :color="res.payment_status === 'مدفوع' ? 'success lighten-4 green--text text--darken-4' : 'error lighten-4 red--text text--darken-4'" style="opacity: 1 !important;">
                                      {{ res.payment_status }}
                                    </v-chip>
                                  </td>
                                  <td class="text-center">
                                    <v-chip x-small class="font-weight-bold" :color="getBoardingChipColor(res.boarding_status)" dark style="opacity: 1 !important;">
                                      {{ res.boarding_status_label }}
                                    </v-chip>
                                  </td>
                                  <td class="text-center caption text-grey-darken-1">{{ res.created_at }}</td>
                                </tr>

                                <tr v-if="!expandedDetails.reservations || expandedDetails.reservations.length === 0">
                                  <td colspan="12" class="text-center py-3 grey--text">لا توجد حجوزات مسجلة لهذه الرحلة.</td>
                                </tr>
                              </tbody>
                            </v-simple-table>
                          </v-card>
                        </td>
                      </tr>
                    </template>

                    <tr v-if="tripsTableRows.length === 0">
                      <td colspan="13" class="text-center py-6 text--secondary">
                        <v-icon size="36" color="grey lighten-1">mdi-clipboard-text-off-outline</v-icon>
                        <div class="mt-1">لا توجد رحلات تطابق الفلاتر المحددة.</div>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- Pagination for Tab 1 -->
              <div class="d-flex align-center justify-space-between pt-3 px-2" v-if="tripsPagination.last_page > 1">
                <div class="caption text-grey-darken-2 font-weight-bold">
                  عرض صفحة {{ tripsPagination.page }} من {{ tripsPagination.last_page }} (إجمالي {{ tripsPagination.total }} رحلة)
                </div>
                <v-pagination
                  v-model="tripsPagination.page"
                  :length="tripsPagination.last_page"
                  :total-visible="7"
                  circle
                  color="purple darken-2"
                  @input="fetchReport"
                ></v-pagination>
              </div>
            </v-tab-item>

            <!-- TAB 2: Bookings & Passengers Master List -->
            <v-tab-item class="pa-3">
              <v-simple-table class="report-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-center font-weight-bold">تاريخ الرحلة</th>
                      <th class="text-center font-weight-bold">الوقت</th>
                      <th class="text-center font-weight-bold">اسم العميل</th>
                      <th class="text-center font-weight-bold">رقم العميل</th>
                      <th class="text-center font-weight-bold">عدد الركاب</th>
                      <th class="text-center font-weight-bold">السائق</th>
                      <th class="text-center font-weight-bold">محطة الركوب</th>
                      <th class="text-center font-weight-bold">محطة النزول</th>
                      <th class="text-center font-weight-bold">وسيلة الدفع</th>
                      <th class="text-center font-weight-bold">المبلغ</th>
                      <th class="text-center font-weight-bold">حالة الدفع</th>
                      <th class="text-center font-weight-bold">حالة الركوب</th>
                      <th class="text-center font-weight-bold">حالة الرحلة</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="b in bookingsTableRows" :key="b.reservation_id + '-' + b.ticket_number">
                      <td class="text-center font-weight-bold">{{ b.trip_date }}</td>
                      <td class="text-center font-weight-bold">{{ tConvert(b.trip_time) }}</td>
                      <td class="text-center font-weight-bold primary--text">{{ b.customer_name }}</td>
                      <td class="text-center font-weight-bold">{{ b.customer_phone }}</td>
                      <td class="text-center font-weight-bold">1</td>
                      <td class="text-center font-weight-bold text-dark">{{ b.driver_name }}</td>
                      <td class="text-center caption font-weight-bold primary--text">{{ b.start_stop }}</td>
                      <td class="text-center caption font-weight-bold grey--text text--darken-3">{{ b.end_stop }}</td>
                      <td class="text-center font-weight-bold">
                        <v-chip x-small :color="b.payment_method === 'كاش' ? 'teal lighten-5 teal--text text--darken-4' : 'purple lighten-5 purple--text text--darken-4'" class="font-weight-bold" style="opacity: 1 !important;">
                          {{ b.payment_method }}
                        </v-chip>
                      </td>
                      <td class="text-center font-weight-bold text-dark">{{ formatCurrency(b.collected_amount || b.price) }}</td>
                      <td class="text-center">
                        <v-chip x-small class="font-weight-bold" :color="b.payment_status === 'مدفوع' ? 'success lighten-4 green--text text--darken-4' : 'error lighten-4 red--text text--darken-4'" style="opacity: 1 !important;">
                          {{ b.payment_status }}
                        </v-chip>
                      </td>
                      <td class="text-center">
                        <v-chip x-small class="font-weight-bold" :color="getBoardingChipColor(b.boarding_status)" dark style="opacity: 1 !important;">
                          {{ b.boarding_status_label }}
                        </v-chip>
                      </td>
                      <td class="text-center caption font-weight-bold">{{ b.trip_status_label }}</td>
                    </tr>

                    <tr v-if="bookingsTableRows.length === 0">
                      <td colspan="13" class="text-center py-6 text--secondary">
                        <v-icon size="36" color="grey lighten-1">mdi-account-search-outline</v-icon>
                        <div class="mt-1">لا توجد حجوزات مسجلة لهذه الرحلة بالفترة المحددة.</div>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- Pagination for Tab 2 -->
              <div class="d-flex align-center justify-space-between pt-3 px-2" v-if="bookingsPagination.last_page > 1">
                <div class="caption text-grey-darken-2 font-weight-bold">
                  عرض صفحة {{ bookingsPagination.page }} من {{ bookingsPagination.last_page }} (إجمالي {{ bookingsPagination.total }} حجز)
                </div>
                <v-pagination
                  v-model="bookingsPagination.page"
                  :length="bookingsPagination.last_page"
                  :total-visible="7"
                  circle
                  color="purple darken-2"
                  @input="fetchReport"
                ></v-pagination>
              </div>
            </v-tab-item>

            <!-- TAB 3: Historical Drivers Summary -->
            <v-tab-item class="pa-3">
              <v-simple-table class="report-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-center font-weight-bold">اسم السائق</th>
                      <th class="text-center font-weight-bold">رقم الهاتف</th>
                      <th class="text-center font-weight-bold">مرات التشغيل</th>
                      <th class="text-center font-weight-bold">الرحلات المنفذة</th>
                      <th class="text-center font-weight-bold">غير المنفذة</th>
                      <th class="text-center font-weight-bold">أول تاريخ تشغيل</th>
                      <th class="text-center font-weight-bold">آخر تاريخ تشغيل</th>
                      <th class="text-center font-weight-bold">إجمالي الحجوزات</th>
                      <th class="text-center font-weight-bold">إجمالي الركاب</th>
                      <th class="text-center font-weight-bold">الكاش المحصل</th>
                      <th class="text-center font-weight-bold">الإلكتروني</th>
                      <th class="text-center font-weight-bold">إجمالي التحصيلات</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="d in driversSummaryList"
                      :key="d.driver_id"
                      class="driver-summary-row cursor-pointer"
                      @click="filterByDriver(d.driver_id)"
                    >
                      <td class="text-center font-weight-bold primary--text">
                        <v-icon small color="primary" class="me-1">mdi-account</v-icon>
                        {{ d.driver_name }}
                      </td>
                      <td class="text-center font-weight-bold">{{ d.driver_phone }}</td>
                      <td class="text-center font-weight-bold">{{ d.total_runs }}</td>
                      <td class="text-center font-weight-bold text-success-dark">{{ d.completed_runs }}</td>
                      <td class="text-center font-weight-bold text-error-dark">{{ d.not_completed_runs }}</td>
                      <td class="text-center caption font-weight-bold">{{ d.first_operating_date }}</td>
                      <td class="text-center caption font-weight-bold">{{ d.last_operating_date }}</td>
                      <td class="text-center font-weight-bold">{{ d.bookings_count }}</td>
                      <td class="text-center font-weight-bold">{{ d.passengers_count }}</td>
                      <td class="text-center font-weight-bold text-success-dark">{{ formatCurrency(d.cash_total) }}</td>
                      <td class="text-center font-weight-bold text-purple-darken-3">{{ formatCurrency(d.other_total) }}</td>
                      <td class="text-center font-weight-bold purple--text text--darken-4">{{ formatCurrency(d.grand_total) }}</td>
                    </tr>

                    <tr v-if="driversSummaryList.length === 0">
                      <td colspan="12" class="text-center py-6 text--secondary">
                        <v-icon size="36" color="grey lighten-1">mdi-account-off-outline</v-icon>
                        <div class="mt-1">لا يوجد سائقون مسجلون لهذه الرحلة بالفترة المحددة.</div>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-3 bg-white d-flex justify-space-between align-center">
        <div class="caption font-weight-bold text-grey-darken-2">
          BEEPGO Reporting Center — جميع البيانات محدثة وحقيقية من قاعدة البيانات
        </div>
        <v-btn color="grey darken-2" text font-weight-bold @click="closeModal">إغلاق</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'TripReportModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    trip: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      internalDialog: false,
      loading: false,
      loadingExcel: false,
      loadingCsv: false,
      loadingDetails: false,
      errorMessage: null,
      activeTab: 0,

      // Filter States
      selectedPeriod: 'month',
      dateFrom: null,
      dateTo: null,
      searchQuery: '',
      selectedDriver: null,
      selectedStatus: 'all',
      selectedPaymentMethod: 'all',

      // Response Data
      tripInfo: null,
      kpis: null,
      tripsTableRows: [],
      tripsPagination: { total: 0, page: 1, per_page: 25, last_page: 1 },
      bookingsTableRows: [],
      bookingsPagination: { total: 0, page: 1, per_page: 25, last_page: 1 },
      driversSummaryList: [],
      availableDrivers: [],

      // In-line Expand Details
      expandedRowId: null,
      expandedDetails: null,

      searchTimer: null,

      periodOptions: [
        { label: 'اليوم', value: 'today' },
        { label: 'الأسبوع', value: 'week' },
        { label: 'الشهر', value: 'month' },
        { label: 'السنة', value: 'year' },
        { label: 'آخر 7 أيام', value: 'last_7_days' },
        { label: 'آخر 30 يوم', value: 'last_30_days' },
        { label: 'فترة مخصصة', value: 'custom' },
      ],

      statusOptions: [
        { label: 'كل الحالات', value: 'all' },
        { label: 'منفذة', value: 'completed' },
        { label: 'غير منفذة / ملغاة', value: 'not_completed' },
      ],

      paymentMethodOptions: [
        { label: 'كل وسائل الدفع', value: 'all' },
        { label: 'كاش', value: 'cash' },
        { label: 'إلكتروني / Online', value: 'online' },
      ],
    };
  },
  computed: {
    reportTripTitle() {
      if (this.tripInfo && this.tripInfo.display_title) {
        return this.tripInfo.display_title;
      }
      if (this.tripInfo && this.tripInfo.first_stop_name && this.tripInfo.last_stop_name && this.tripInfo.first_stop_name !== this.tripInfo.last_stop_name) {
        return `${this.tripInfo.first_stop_name} ← ${this.tripInfo.last_stop_name}`;
      }
      if (this.trip && this.trip.route && this.trip.route.name) {
        return this.trip.route.name;
      }
      if (this.tripInfo && this.tripInfo.route_name) {
        return this.tripInfo.route_name;
      }
      if (this.trip && this.trip.id) {
        return `رحلة #${this.trip.id}`;
      }
      return '';
    },
    currentRouteName() {
      if (this.trip && this.trip.route && this.trip.route.name) return this.trip.route.name;
      if (this.tripInfo && this.tripInfo.route_name) return this.tripInfo.route_name;
      return '-';
    },
    currentFirstStopTime() {
      if (this.trip && this.trip.first_stop_time) return this.trip.first_stop_time;
      if (this.tripInfo && this.tripInfo.first_stop_time) return this.tripInfo.first_stop_time;
      return '';
    },
    currentRepetitionLabel() {
      if (this.tripInfo && this.tripInfo.repetition_label) return this.tripInfo.repetition_label;
      if (this.trip && this.trip.repetition_period !== undefined && this.trip.repetition_period !== null) {
        if (this.trip.repetition_period === 0) return 'لا تتكرر';
        if (this.trip.repetition_period === 1) return 'يومياً';
        if (this.trip.repetition_period === 7) return 'أسبوعياً';
        return `كل ${this.trip.repetition_period} يوم`;
      }
      return '-';
    },
    driverSelectItems() {
      const items = [{ label: 'كل السائقين', value: null }];
      if (this.availableDrivers && this.availableDrivers.length > 0) {
        this.availableDrivers.forEach((d) => {
          items.push({ label: d.name + ' (' + d.phone + ')', value: d.id });
        });
      }
      return items;
    },
  },
  watch: {
    value(newVal) {
      this.internalDialog = newVal;
      if (newVal) {
        this.resetStateForNewTrip();
        if (this.trip) {
          this.fetchReport();
        }
      }
    },
    internalDialog(newVal) {
      this.$emit('input', newVal);
      if (!newVal) {
        this.resetStateForNewTrip();
      }
    },
    trip(newTrip, oldTrip) {
      if (newTrip && (!oldTrip || newTrip.id !== oldTrip.id)) {
        this.resetStateForNewTrip();
        if (this.internalDialog) {
          this.fetchReport();
        }
      }
    },
  },
  created() {
    this.internalDialog = this.value;
  },
  methods: {
    fetchReport() {
      if (!this.trip || !this.trip.id) return;

      this.loading = true;
      this.errorMessage = null;
      const params = {
        period: this.selectedPeriod,
        driver_id: this.selectedDriver,
        status: this.selectedStatus,
        payment_method: this.selectedPaymentMethod,
        search: this.searchQuery,
        page: this.tripsPagination.page,
        per_page: this.tripsPagination.per_page,
        bookings_page: this.bookingsPagination.page,
        bookings_per_page: this.bookingsPagination.per_page,
      };

      if (this.selectedPeriod === 'custom') {
        if (this.dateFrom) params.date_from = this.dateFrom;
        if (this.dateTo) params.date_to = this.dateTo;
      }

      axios
        .get(`/trips/${this.trip.id}/report`, { params })
        .then((response) => {
          if (response.data && response.data.success) {
            const res = response.data.data;
            this.tripInfo = res.trip_info;
            this.kpis = res.kpis;
            this.tripsTableRows = res.trips_table.data || [];
            this.tripsPagination = {
              total: res.trips_table.total,
              page: res.trips_table.page,
              per_page: res.trips_table.per_page,
              last_page: res.trips_table.last_page,
            };
            this.bookingsTableRows = res.bookings_table.data || [];
            this.bookingsPagination = {
              total: res.bookings_table.total,
              page: res.bookings_table.page,
              per_page: res.bookings_table.per_page,
              last_page: res.bookings_table.last_page,
            };
            this.driversSummaryList = res.drivers_summary || [];
            this.availableDrivers = res.available_drivers || [];
          } else {
            this.errorMessage = (response.data && response.data.message) ? response.data.message : 'فشل في تحميل بيانات التقرير.';
          }
        })
        .catch((error) => {
          console.error('Error fetching trip report:', error);
          const msg = (error.response && error.response.data && error.response.data.message)
            ? error.response.data.message
            : (error.message || 'حدث خطأ غير متوقع أثناء الاتصال بالخادم.');
          this.errorMessage = 'فشل في تحميل بيانات التقرير: ' + msg;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    selectPeriod(periodVal) {
      this.selectedPeriod = periodVal;
      this.tripsPagination.page = 1;
      this.bookingsPagination.page = 1;
      this.fetchReport();
    },

    debouncedFetch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.tripsPagination.page = 1;
        this.bookingsPagination.page = 1;
        this.fetchReport();
      }, 400);
    },

    filterByDriver(driverId) {
      this.selectedDriver = driverId;
      this.activeTab = 0; // Switch to trips tab
      this.fetchReport();
    },

    toggleExpandRow(pt) {
      if (this.expandedRowId === pt.planned_trip_id) {
        this.expandedRowId = null;
        this.expandedDetails = null;
        return;
      }

      this.expandedRowId = pt.planned_trip_id;
      this.expandedDetails = null;
      this.loadingDetails = true;

      axios
        .get(`/planned-trips/${pt.planned_trip_id}/report-bookings`)
        .then((response) => {
          if (response.data && response.data.success) {
            this.expandedDetails = response.data.data;
          }
        })
        .catch((err) => {
          console.error('Error loading expanded booking details:', err);
        })
        .finally(() => {
          this.loadingDetails = false;
        });
    },

    resetStateForNewTrip() {
      this.tripInfo = null;
      this.kpis = null;
      this.tripsTableRows = [];
      this.bookingsTableRows = [];
      this.driversSummaryList = [];
      this.expandedRowId = null;
      this.expandedDetails = null;
      this.errorMessage = null;
    },

    exportCsv() {
      if (!this.trip || !this.trip.id) return;

      this.loadingCsv = true;
      const params = {
        period: this.selectedPeriod,
        driver_id: this.selectedDriver,
        status: this.selectedStatus,
        payment_method: this.selectedPaymentMethod,
        search: this.searchQuery,
      };

      if (this.selectedPeriod === 'custom') {
        if (this.dateFrom) params.date_from = this.dateFrom;
        if (this.dateTo) params.date_to = this.dateTo;
      }

      axios
        .get(`/trips/${this.trip.id}/report/export-csv`, {
          params,
          responseType: 'blob',
        })
        .then((response) => {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', `trip-report-${this.trip.id}-${moment().format('YYYY-MM-DD')}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch(async (err) => {
          console.error('Error exporting CSV:', err);
          let errorText = 'فشل تصدير ملف CSV. يرجى المحاولة مرة أخرى.';
          if (err.response && err.response.data) {
            try {
              if (err.response.data instanceof Blob) {
                const text = await err.response.data.text();
                const json = JSON.parse(text);
                if (json && json.message) errorText = json.message;
              } else if (err.response.data.message) {
                errorText = err.response.data.message;
              }
            } catch (e) {}
          }
          this.errorMessage = errorText;
        })
        .finally(() => {
          this.loadingCsv = false;
        });
    },

    closeModal() {
      this.internalDialog = false;
      this.resetStateForNewTrip();
    },

    tConvert(time) {
      if (!time) return '';
      return moment(time, 'HH:mm:ss').format('h:mm A');
    },

    formatCurrency(val) {
      const num = parseFloat(val) || 0;
      return num.toLocaleString('ar-EG') + ' ج.م';
    },

    getStatusChipColor(status) {
      if (status === 'completed') return 'success';
      if (status === 'cancelled' || status === 'unexecuted_past') return 'error';
      if (status === 'running') return 'info';
      return 'warning';
    },

    getBoardingChipColor(status) {
      if (status === 'boarded') return 'success';
      if (status === 'no_show') return 'error';
      return 'grey darken-1';
    },
  },
};
</script>

<style scoped>
.trip-report-card {
  font-family: inherit;
  color: #111827 !important;
}

.kpi-card {
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(107, 33, 168, 0.12);
}

.border-purple { border-left: 4px solid #6b21a8 !important; }
.border-success { border-left: 4px solid #16a34a !important; }
.border-error { border-left: 4px solid #dc2626 !important; }
.border-info { border-left: 4px solid #2563eb !important; }
.border-cyan { border-left: 4px solid #0891b2 !important; }
.border-cash { border-left: 4px solid #0d9488 !important; }
.border-online { border-left: 4px solid #9333ea !important; }
.border-total { border-left: 4px solid #4c1d95 !important; }

.text-dark { color: #111827 !important; opacity: 1 !important; }
.text-success-dark { color: #15803d !important; opacity: 1 !important; }
.text-error-dark { color: #b91c1c !important; opacity: 1 !important; }

.report-table th {
  background-color: #f9fafb !important;
  color: #1f2937 !important;
  font-size: 0.85rem !important;
  border-bottom: 2px solid #e5e7eb !important;
}

.report-table td {
  color: #111827 !important;
  font-size: 0.875rem !important;
  padding: 10px 8px !important;
  border-bottom: 1px solid #f3f4f6 !important;
}

.trip-main-row:hover {
  background-color: #f3e8ff !important;
}

.driver-summary-row:hover {
  background-color: #faf5ff !important;
}

.btn-period {
  text-transform: none !important;
  letter-spacing: normal !important;
}

.border-purple-light {
  border: 1px solid #e9d5ff !important;
}
</style>
